/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  ModerationState, ProfileStatus, Redirects, UpdateUserPreferencesRequest, UserInfoPayloadResponse,
} from 'src/types/UserInfo';
import FetchStatus from 'src/types/FetchStatus';
import { PERSONALIZATION_PREFERENCES_API_ENDPOINT, PERSONALIZATION_PREFERENCES_API_V2_ENDPOINT, USER_INFO_API_ENDPOINT } from 'src/constants/apiEndpoints';
import {
  customFetch,
  getEnvironment,
  validateMarketplaceId,
  updateConfig,
  getMpNameFromId,
  getDefaultMarketplace,
} from 'src/utils';
import { getConfigValue } from 'src/utils/localStorage';
import { MARKETPLACE_URL_TO_ID_MAP, MARKETPLACES } from 'src/constants/marketplaces';
import { ENVIRONMENT } from 'src/constants/environments';
import LocalStorageKeys from 'src/utils/localStorageKeys';
import { DateRangeType, SortByType } from 'src/components/search-listing/utils/types';
import {
  dateRangeValidation,
  getDefaultThreadListingDateRange,
  getDefaultThreadListingSortBy,
  sortByValidation,
} from 'src/utils/userPreferences';
import { GetUserDataResponse } from 'src/types/server/BaseRequest';
import WeblabId from 'src/weblab/weblabId';
import type { AppDispatch, AppState } from '../store';
import { selectIsWeblabIdInT1 } from './weblab';

interface UserPreferences {
  threadListingSortBy: SortByType
  threadListingDateRange: DateRangeType
}

interface SetUserInfoProps {
  state: UserInfoState,
  payload: UserInfoPayloadResponse,
  savedMarketplaceId?: string,
  shouldSyncUserPreference?: boolean
  userPreferences: Partial<UserPreferences>
}

interface UpdateMarketplaceAction {
  marketplaceId: string,
  dispatch: AppDispatch,
  isPersistedPreference: boolean,
  replaceURL?: string
}

export interface UserInfoState {
  username: string,
  userId?: string,
  avatarSrc?: string,
  marketplaceIds: string[],
  isInternal: boolean,
  profileStatus: ProfileStatus,
  status: FetchStatus,
  error?: string
  redirectTo?: Redirects,
  selectedMarketplaceId: string,
  previousSelectedMarketplaceId?: string,
  hasWriteAccess: boolean,
  moderationState?: ModerationState
  userToken?: string
  userPreferences: UserPreferences
  userPreferenceStatus: FetchStatus,
  selectedMarketplaceIdStatus: FetchStatus,
  shouldSyncUserPreference: boolean
}

const getIsThreadDetailSSR = (state: AppState) => selectIsWeblabIdInT1(
  state,
  WeblabId.SELLER_FORUMS_THREAD_DETAILS_SSR,
);

export const getInitialSelectedMarketplaceId = (userId?: string) => {
  // Get marketplaceId from localstorage.
  const localStoreMarketplaceId = getConfigValue(
    LocalStorageKeys.marketplaceId,
    validateMarketplaceId,
    userId,
  );
  if (localStoreMarketplaceId) {
    return localStoreMarketplaceId;
  }

  // Get marketplaceId from the domain.
  if (typeof window !== 'undefined') {
    const domainMarketplaceId = MARKETPLACE_URL_TO_ID_MAP[window.location.hostname];
    if (getMpNameFromId(domainMarketplaceId) != null) {
      updateConfig(LocalStorageKeys.marketplaceId, domainMarketplaceId, userId);
      return domainMarketplaceId;
    }
  }

  // Get the default marketplaceId for the region.
  const defaultMarketplaceId = MARKETPLACES[getDefaultMarketplace()][getEnvironment(ENVIRONMENT)]!;
  updateConfig(LocalStorageKeys.marketplaceId, defaultMarketplaceId, userId);
  return defaultMarketplaceId;
};

const getThreadListingSortBy = (profileStatus: ProfileStatus, userId?: string) => {
  const localStorageThreadListingSortBy = getConfigValue(
    LocalStorageKeys.discussionListingSortBy,
    sortByValidation,
    userId,
  );

  if (localStorageThreadListingSortBy) return localStorageThreadListingSortBy;
  return getDefaultThreadListingSortBy(profileStatus);
};

const getThreadListingDateRange = (userId?: string) => {
  const localStorageThreadListingDateRange = getConfigValue(
    LocalStorageKeys.discussionListingDateRange,
    dateRangeValidation,
    userId,
  );
  if (localStorageThreadListingDateRange) return localStorageThreadListingDateRange;
  return getDefaultThreadListingDateRange();
};

export const initialState: UserInfoState = {
  username: '',
  isInternal: false,
  marketplaceIds: [],
  profileStatus: ProfileStatus.unauthenticated,
  status: FetchStatus.idle,
  selectedMarketplaceId: getInitialSelectedMarketplaceId(),
  hasWriteAccess: false,
  userPreferences: {
    threadListingSortBy: getDefaultThreadListingSortBy(ProfileStatus.unauthenticated),
    threadListingDateRange: getDefaultThreadListingDateRange(),
  },
  userPreferenceStatus: FetchStatus.idle,
  selectedMarketplaceIdStatus: FetchStatus.idle,
  shouldSyncUserPreference: false,
};

export const fetchUserInfo = createAsyncThunk(
  'app/fetchUserInfo',
  async (args, { rejectWithValue }) => {
    const userInfoResponse = await customFetch(
      USER_INFO_API_ENDPOINT,
      undefined,
      (error: Error) => rejectWithValue(error.message),
      1,
    ) as UserInfoPayloadResponse;
    return userInfoResponse;
  },
);

export const updateUserPreferences = createAsyncThunk(
  'personalization/updateUserPreference',
  async (
    request: UpdateUserPreferencesRequest,
    { rejectWithValue, getState },
  ) => customFetch(
    getIsThreadDetailSSR(getState() as AppState)
      ? PERSONALIZATION_PREFERENCES_API_V2_ENDPOINT
      : PERSONALIZATION_PREFERENCES_API_ENDPOINT,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
    (error: Error) => rejectWithValue(error.message),
  ),
);

const setUserInfo = (
  {
    state,
    payload,
    savedMarketplaceId,
    userPreferences,
    shouldSyncUserPreference = false,
  }: SetUserInfoProps,
) => {
  const {
    userInfo, profileStatus, redirectTo, userToken,
  } = payload;
  const status = FetchStatus.complete;
  const userPreferencesValues: UserPreferences = {
    threadListingSortBy: userPreferences.threadListingSortBy ?? getThreadListingSortBy(
      payload.profileStatus,
      payload.userInfo?.userId,
    ),
    threadListingDateRange: userPreferences.threadListingDateRange
    ?? getThreadListingDateRange(payload.userInfo?.userId),
  };
  const userInfoState: Partial<UserInfoState> = {
    status,
    profileStatus,
    redirectTo,
    userToken,
    userPreferences: userPreferencesValues,
    shouldSyncUserPreference,
  };

  if (userInfo) {
    const {
      username, userId, internalUser, avatarSrc, marketplaceIds = [], moderationState,
    } = userInfo;
    const selectedMarketplaceId = savedMarketplaceId || getInitialSelectedMarketplaceId(userId);
    const hasWriteAccess = !!internalUser
    || marketplaceIds.includes(selectedMarketplaceId);

    Object.assign(state, {
      ...userInfoState,
      username: username || initialState.username,
      userId,
      marketplaceIds,
      isInternal: internalUser || false,
      avatarSrc,
      hasWriteAccess,
      moderationState,
      selectedMarketplaceId,
    });
  } else {
    Object.assign(state, {
      ...userInfoState,
      selectedMarketplaceId: savedMarketplaceId || state.selectedMarketplaceId,
    });
  }
};

const userInfoSlice = createSlice({
  name: 'userInfo',
  initialState,
  reducers: {
    setUsername: (
      state: UserInfoState,
      action: PayloadAction<string>,
    ) => {
      state.username = action.payload;
    },
    setUserId: (
      state: UserInfoState,
      action: PayloadAction<string>,
    ) => {
      state.userId = action.payload;
    },
    setAvatarSrc: (
      state: UserInfoState,
      action: PayloadAction<string>,
    ) => {
      state.avatarSrc = action.payload;
    },
    setPrevMarketplace: (state: UserInfoState, action: PayloadAction<string>) => {
      state.previousSelectedMarketplaceId = action.payload;
    },
    changeMarketplace: (
      state: UserInfoState,
      action: PayloadAction<string>,
    ) => {
      state.selectedMarketplaceId = action.payload;
      updateConfig(LocalStorageKeys.marketplaceId, action.payload, state.userId);
      state.hasWriteAccess = !!state.isInternal || state.marketplaceIds.includes(action.payload);
    },
    setUserInfoState: (
      state: UserInfoState,
      action: PayloadAction<GetUserDataResponse>,
    ) => {
      setUserInfo({
        state,
        payload: action.payload,
        savedMarketplaceId: action.payload.selectedMarketplaceId,
        userPreferences: {
          threadListingSortBy: action.payload.sortBy,
          threadListingDateRange: action.payload.dateRange,
        },
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.rejected, (state: UserInfoState, action) => {
        state.error = action.payload as string;
        state.status = FetchStatus.failed;
      })
      .addCase(fetchUserInfo.pending, (state: UserInfoState) => {
        state.status = FetchStatus.loading;
      })
      .addCase(fetchUserInfo.fulfilled, (state: UserInfoState, action) => {
        const userPreferences = {
          threadListingDateRange: action.payload.userInfo?.dateRangePref,
          threadListingSortBy: action.payload.userInfo?.sortByPref,
        };
        const savedMarketplaceId = action.payload.userInfo?.lastSelectedMarketplaceId;
        const isRegistered = action.payload.profileStatus === ProfileStatus.registered;
        setUserInfo({
          state,
          payload: action.payload,
          userPreferences,
          savedMarketplaceId,
          shouldSyncUserPreference: isRegistered && !savedMarketplaceId,
        });
      })
      .addCase(updateUserPreferences.rejected, (state: UserInfoState, action) => {
        if (action.meta.arg?.lastSelectedMarketplaceId) {
          state.selectedMarketplaceIdStatus = FetchStatus.failed;
        } else if (action.meta.arg?.sortByPref
          || action.meta.arg?.dateRangePref) {
          state.userPreferenceStatus = FetchStatus.failed;
        }
      })
      .addCase(updateUserPreferences.pending, (state: UserInfoState, action) => {
        if (action.meta.arg.lastSelectedMarketplaceId) {
          state.selectedMarketplaceIdStatus = FetchStatus.loading;
        } else if (action.meta.arg.sortByPref
          || action.meta.arg.dateRangePref) {
          state.userPreferenceStatus = FetchStatus.loading;
        }
      })
      .addCase(updateUserPreferences.fulfilled, (state: UserInfoState, action) => {
        if (action.meta.arg.lastSelectedMarketplaceId
          && (!action.meta.arg.sortByPref && !action.meta.arg.dateRangePref)) {
          state.selectedMarketplaceIdStatus = FetchStatus.complete;
        } else if (!action.meta.arg.lastSelectedMarketplaceId
          && (action.meta.arg.sortByPref
          || action.meta.arg.dateRangePref)) {
          const { threadListingDateRange, threadListingSortBy } = state.userPreferences;
          state.userPreferences = {
            threadListingDateRange: action.meta.arg.dateRangePref ?? threadListingDateRange,
            threadListingSortBy: action.meta.arg.sortByPref ?? threadListingSortBy,
          };
          state.userPreferenceStatus = FetchStatus.complete;
        } else {
          state.shouldSyncUserPreference = false;
        }
      });
  },
});

export const updateMarketplaceAction = (
  {
    marketplaceId,
    dispatch,
    isPersistedPreference,
    replaceURL,
  }: UpdateMarketplaceAction,
) => {
  dispatch(userInfoSlice.actions.changeMarketplace(marketplaceId));
  if (isPersistedPreference) {
    dispatch(updateUserPreferences({ lastSelectedMarketplaceId: marketplaceId }))
      .then(() => {
        if (replaceURL) {
          window.location.replace(replaceURL);
        }
      });
  } else if (replaceURL) {
    window.location.replace(replaceURL);
  }
};

export const userInfoActions = userInfoSlice.actions;

export const selectMarketplaceId = (state: AppState) => state.userInfo.selectedMarketplaceId!;

export const getUserId = (state: AppState) => state.userInfo.userId!;

export const selectUserPreferences = (state: AppState) => state.userInfo.userPreferences;

export default userInfoSlice.reducer;
